import { Box, Button, Grid, Skeleton, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useRecoilValue } from 'recoil';
import { BEEScorecardService } from '../../Component/Api/BEEScorecard/BEEScorecard';
import { CoreExportService } from '../../Component/Api/Export/CoreExportService';
import { IndividualService } from "../../Component/Api/Individual/IndividualService";
import { CoreStaticDataService } from '../../Component/Api/StaticData/CoreStaticDataService';
import { ConfirmationDialog } from '../../Component/Common/Components/ConfirmationDialog';
import { DataTable } from '../../Component/Common/Components/DataTable/DataTable';
import { FilterType, IFilter } from '../../Component/Common/Components/DataTable/Filters';
import { InformationDialog } from '../../Component/Common/Components/InformationDialog';
import { MDAlert } from '../../Component/Common/Components/MDAlert';
import { MDBox } from '../../Component/Common/Components/MDBox';
import { MDProgress } from '../../Component/Common/Components/MDProgress';
import { MDTypography } from '../../Component/Common/Components/MDTypography';
import { ColumnSortOption } from '../../Component/Model/Dto/ColumnSortOption';
import { Export } from '../../Component/Model/Dto/Export';
import { Individual } from "../../Component/Model/Dto/Individual";
import { filterColumnValues } from '../../Component/Model/Dto/ScorecardLicense';
import { ExportKeyValue } from '../../Component/Model/Dto/Upload';
import { DataImportType, EmployeeTypes } from '../../Component/Model/Enum/BEE123Enums';
import { EntriesPerPage } from '../../Component/Model/Table/Table';
import { defaultObject, emptyGuid } from '../../Helpers/typescriptHelper';
import { UserAtom } from '../../State/user.Atom';
import config from '../../config';
import { IndividualCentralDataModal } from './IndividualModal/IndividualCentralDataModal';
import { IndividualTableMapper } from './IndividualTableMapper';
import {useSignalRConnection} from "../../Hooks/useSignalRConnection";

export const IndividualDataTableWithActions = () => {
    const user = useRecoilValue(UserAtom);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [individualData, setIndividualData] = useState<Individual.IndividualSummaryDto[]>([]);
    const [showCreatePeopleModal, setShowCreatePeopleModal] = useState<boolean>(false);
    const [individualId, setIndividualId] = useState<number>(0);
    const [filteredItemsCount, setFilteredItemsCount] = useState<number>(0);
    const [totalItemsCount, setTotalItemsCount] = useState<number>(0);

    const [showInformationDialog, setShowInformationDialog] = useState(false);
    const [showConfirmationDialog, setShowConfirmationDialog] = useState<boolean>(false);
    const [isConfirmationLoading, setIsConfirmationLoading] = useState<boolean>(false);
    const [scorecardNames, setScorecardNames] = useState<string[]>([]);
    const [informationMessage, setInformationMessage] = useState('');
    const [scorcardId, setScorcardId] = useState<string>('');
    const [progress, setProgress] = useState(0);
    const [fileExporting, setFileExporting] = useState<boolean>(false);
    const [isEditable, setIsEditable] = useState<boolean>(false);

    const [pageNumber, setPageNumber] = useState<number>(1);
    const [searchValue, setSearchValue] = useState<string>("");
    const [entriesPerPage, setEntriesPerPage] = useState<EntriesPerPage>({ defaultValue: 15 });
    const [columnSortOptions, setColumnSortOptions] = useState<ColumnSortOption[]>([]);
    const [filters, setFilters] = useState({});
    const [filterScorecardTypes, setFilterScorecardTypes] = useState<filterColumnValues[]>(defaultObject);
    const [filterScorecardIds, setfilterScorecardIds] = useState<filterColumnValues[]>(defaultObject);
    const hub = useSignalRConnection("ImportHub");

    const individualSearchRequest: Individual.IndividualSearchRequest = {
        pageNumber: pageNumber,
        pageSize: entriesPerPage.defaultValue!,
        searchValue: searchValue,
        registrationId: emptyGuid,
        scorecardIds: [],
        genderIds: [],
        raceIds: [],
        employeeTypeIds: [],
        disabledValues: [],
        occupationalLevelIds: [],
        provinceIds: [],
        isYesEmployeeValues: [],
        isYesAbsorbedValues: [],
        scorecardTypes: [],
        hireDateStart: undefined,
        hireDateEnd: undefined,
        terminationDateStart: undefined,
        terminationDateEnd: undefined,
        columnSortOptions: columnSortOptions,
        employeeTypeIdsToIncludeForEE: [EmployeeTypes.Employee, EmployeeTypes.Employee_Previously_Unemployed]
    }

    const peopleFilters: Array<IFilter> = [
        {
            label: 'Gender:',
            values: [{ key: 'Male', val: 1 }, { key: 'Female', val: 2 }],
            type: FilterType.multiselect, propname: 'genderIds'
        },
        {
            label: "Scorecard Type:",
            values: filterScorecardTypes,
            type: FilterType.multiselect,
            propname: 'scorecardTypes',
        },
        {
            label: "Scorecard Name:",
            values: filterScorecardIds,
            type: FilterType.multiselect,
            propname: 'scorecardIds',
        },
        {
            label: 'Race:',
            values: [{ key: 'African', val: 1 }, { key: 'Coloured', val: 2 }, { key: 'Indian', val: 3 }, { key: 'White', val: 4 }, { key: 'Non South African', val: 5 }],
            type: FilterType.multiselect, propname: 'raceIds',
        },
        {
            label: 'Occupation Level:',
            values: [{ key: 'Executive Director', val: 1 },
            { key: 'Non Executive Director', val: 2 },
            { key: 'Executive Management', val: 3 },
            { key: 'Other Executive Management', val: 4 },
            { key: 'Senior Management', val: 5 },
            { key: 'Middle Management', val: 6 },
            { key: 'Junior Management', val: 7 },
            { key: 'Semi skilled', val: 8 },
            { key: 'Unskilled', val: 9 },
            { key: 'Unspecified', val: 10 }],
            type: FilterType.multiselect, propname: 'occupationalLevelIds'

        },
        {
            label: 'Employee Type:',
            values: [
                { key: 'Employed By Other', val: 2 },
                { key: 'Employee', val: 1 },
                { key: 'Employee Previously Unemployed', val: 4 },
                { key: 'Unemployed', val: 3 }],
            type: FilterType.multiselect, propname: 'employeeTypeIds'
        },
        {
            label: 'Disabled:',
            values: [{ key: 'Yes', val: true }, { key: 'No', val: false }],
            type: FilterType.multiselect, propname: 'disabledValues'

        },
        {
            label: 'Province:',
            values: [
                { key: 'Unspecified', val: 0 },
                { key: 'National (NAT)', val: 1 },
                { key: 'Eastern Cape (EC)', val: 2 },
                { key: 'Free State (FS)', val: 3 },
                { key: 'Gauteng (GP)', val: 4 },
                { key: 'KwaZulu-Natal (KZN)', val: 5 },
                { key: 'Limpopo (LP)', val: 6 },
                { key: 'Mpumalanga (MP)', val: 7 },
                { key: 'North West (NW)', val: 8 },
                { key: 'Northern Cape (NC)', val: 9 },
                { key: 'Western Cape (WC)', val: 10 },
            ],
            type: FilterType.multiselect, propname: 'provinceIds'
        },
        {
            label: 'Y.E.S. Employee:',
            values: [{ key: 'Yes', val: true }, { key: 'No', val: false }],
            type: FilterType.multiselect, propname: 'isYesEmployeeValues'

        },
        {
            label: 'Y.E.S. Absorbed:',
            values: [{ key: 'Yes', val: true }, { key: 'No', val: false }],
            type: FilterType.multiselect, propname: 'isYesAbsorbedValues'

        },
        {
            label: 'Start Period',
            values: [],
            type: FilterType.dateRange, propname: 'startDateRange',

        },
        {
            label: 'Termination Period',
            values: [],
            type: FilterType.dateRange, propname: 'endDateRange',
        }
    ];

    const GetScorecardTypes = async () => {
        try {
            const scorecardType = await CoreStaticDataService.getAllScorecardTypes();
            setFilterScorecardTypes(
                scorecardType.data.map((data) => {
                    return { key: data.name, val: data.id };
                })
            );
        } catch (error) {
            console.log(error)
        }
    };

    const GetScorecardNameByReg = async () => {
        try {
            const scorecardNameByReg = await BEEScorecardService.getAllScorecards();
            const beeScorecards = scorecardNameByReg.data.beeScorecards;
            const eeScorecards = scorecardNameByReg.data.eeScorecards;
            const allScorecards = beeScorecards.concat(eeScorecards);
            setfilterScorecardIds(
                allScorecards.map((data) => {
                    return { key: data.scorecardName, val: data.scorecardId };
                })
            );
        }
        catch (error) {
            console.log(error);
        }
    };

    const entriesPerPageChanged = (entriesPerPage: number) => {
        setEntriesPerPage({ defaultValue: entriesPerPage });
        setPageNumber(1);
    }

    const pageNumberChanged = (pageNumber: number) => {
        setPageNumber(pageNumber);
    }

    const searchValueChanged = (value: string) => {
        setSearchValue(value);
        setPageNumber(1);
    }

    const columnSortingChanged = (sortOption: ColumnSortOption) => {
        setColumnSortOptions([sortOption]);
        setPageNumber(1);
    }

    const onEditPeopleIndividualRow = (id: number, scorecardId: string, isEditable: boolean) => {
        setScorcardId(scorecardId)
        setIndividualId(id);
        setIsEditable(isEditable);
        setShowCreatePeopleModal(true);
    }

    const filtersChanged = async (filterProps: any) => {
        let HireDateStart = filterProps['startDateRange']?.start;
        let HireDateEnd = filterProps['startDateRange']?.end;
        let TerminationDateStart = filterProps['endDateRange']?.start;
        let TerminationDateEnd = filterProps['endDateRange']?.end;
        setFilters({ ...filterProps, HireDateStart, HireDateEnd, TerminationDateStart, TerminationDateEnd });
        setPageNumber(1);
    }

    const closeModal = () => {
        fetchData();
        setShowCreatePeopleModal(false);
    }

    const onDeletePeopleRow = async () => {
        setIsConfirmationLoading(true);
        try {
            await IndividualService.Delete(individualId);
            toast.success('Individual deleted successfully');
            fetchData();
        } catch (error: any) {

            if (error.response.status === 400) {
                const response: string = error.response.data.toString();
                if (response !== undefined && response !== null) {
                    setInformationMessage(response.split(";")[0]);
                    setScorecardNames(response.split(";")?.splice(1));
                    setShowInformationDialog(true);
                }
            }
        } finally {
            setIsConfirmationLoading(false);
            setShowConfirmationDialog(false);
        }
    }

    const fetchData = async () => {
        setIsLoading(true);
        try {
            let request = { ...individualSearchRequest, ...filters };
            let resp = await IndividualService.IndividualSearch(request);
            if (resp.data) {
                setIndividualData(resp.data.results);
                setFilteredItemsCount(resp.data.filteredItemsCount);
                setTotalItemsCount(resp.data.totalItemsCount);
            }
        } catch (error) {
            console.log(error);
            setFilteredItemsCount(0);
            setTotalItemsCount(0);
        } finally {
            setIsLoading(false);
        }
    }

    const handleCloseConfirmation = () => {
        setShowConfirmationDialog(false);
        setIndividualId(0);
    }

    const handleDelete = (id: number) => {
        setIndividualId(id);
        setShowConfirmationDialog(true);
    }

    useEffect(() => {
        fetchData();
    }, [entriesPerPage, pageNumber, searchValue, columnSortOptions, filters]);

    useEffect(() => {
        GetScorecardTypes();
        GetScorecardNameByReg();
    }, []);


    useEffect(() => {
        if (config.enable_CDMExportFunctionality === 'false') return;

        (async () => {
            try {
                if(!hub.isConnected)
                {
                    await hub.start();
                }

                hub.on(`exportProgress-${user.username}-${DataImportType[DataImportType.Individual]}`, (data: any) => {
                    setFileExporting(true);
                })
                hub.on(`exportProgressUpdate-${user.username}-${DataImportType[DataImportType.Individual]}`, (data: any) => {
                    setProgress(data.progress);
                    setFileExporting(true);
                })
                hub.on(`exportProgressFailed-${user.username}-${DataImportType[DataImportType.Individual]}`, (data: any) => {
                    setFileExporting(false);
                    toast.error("Export could not be completed");
                })
                hub.on(`exportProgressCompleted-${user.username}-${DataImportType[DataImportType.Individual]}`, (data: any) => {
                    setFileExporting(false);
                    setProgress(0);
                    window.open(data.ExportUrl, "_blank");
                })
            } catch (error) {
                console.error("SignalR connection failed:", error);
            }
        })();
    }, []);

    const onExportData = async () => {
        setIsLoading(true)
        let params: ExportKeyValue[] = [];

        let filterRequest = { ...individualSearchRequest, ...filters };
        for (const [name, value] of Object.entries(filterRequest)) {
            if (!(name === "hireDateStart" || name === "hireDateEnd" || name === "terminationDateStart" || name === "terminationDateEnd")) {
                const tempExportKeyValue = { key: name, value: value?.toString() };
                params.push(tempExportKeyValue);
            }
        }

        const filteredParameter = params.filter(item => item.key !== "registrationId" && item.key !== "endDateRange" && item.key !== "startDateRange");

        const data: Export.DynamicFileExportDto = {
            importType: DataImportType.Individual,
            parameters: filteredParameter
        }
        setFileExporting(true);
        try {
            await CoreExportService.FileExportFunction(data);
        } catch (error) {
            console.log(error);
            toast.error("Export could not be completed");
        } finally {
            params = [];
            setIsLoading(false);
        }
    }

    useEffect(() => {
        fetchData();
    }, [entriesPerPage, pageNumber, searchValue, columnSortOptions, filters]);

    const individualTableData = IndividualTableMapper(individualData, onEditPeopleIndividualRow, handleDelete);

    if (individualTableData === undefined) {
        return (
            <Stack spacing={1}>
                <Skeleton variant="text" />
                <Skeleton variant="circular" width={40} height={40} />
                <Skeleton variant="rectangular" width={210} height={118} />
            </Stack>
        );
    }
    else {
        return (
            <>
                <DataTable
                    key={totalItemsCount}
                    table={individualTableData}
                    filters={peopleFilters}
                    canSearch
                    entriesPerPage={entriesPerPage}
                    pageNumber={pageNumber}
                    filteredItemsCount={filteredItemsCount}
                    totalItemsCount={totalItemsCount}
                    entriesPerPageChanged={entriesPerPageChanged}
                    pageNumberChanged={pageNumberChanged}
                    searchValueChanged={searchValueChanged}
                    isLoading={isLoading}
                    filtersChanged={filtersChanged}
                    manualSorting={true}
                    columnSortingChanged={columnSortingChanged}
                ></DataTable>

                {showCreatePeopleModal &&
                    <IndividualCentralDataModal
                        scorecardId={scorcardId} id={individualId}
                        closeIndividualModal={closeModal}
                        showIndividualModal={showCreatePeopleModal}
                        isEditable={isEditable}
                />}

                <ConfirmationDialog
                    isLoading={isConfirmationLoading}
                    showDialog={showConfirmationDialog}
                    handleAccept={onDeletePeopleRow}
                    handleCancel={handleCloseConfirmation}
                    heading={"Confirm Delete"}
                    message={"Are you sure you want to delete this person?"}
                />
                <InformationDialog
                    handleAccept={() => setShowInformationDialog(false)}
                    heading={"Unable To Delete Person"}
                    message={informationMessage}
                    messageList={scorecardNames}
                    showDialog={showInformationDialog}
                />
                <Grid container >
                    <Grid item xs={12} lg={12}>
                        <MDBox mt={4}>
                            {config.enable_CDMExportFunctionality === 'true' &&
                                <MDAlert color='light'>
                                    {fileExporting ?
                                        <Grid container justifyContent="center" alignItems="center" direction="column">
                                            <Typography variant="h6" mb={1}>Exporting file...</Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                                <Box sx={{ width: '100%', mr: 1 }}>
                                                    <MDProgress variant="determinate" value={progress} style={{ width: '100%' }} />
                                                </Box>
                                                <Box sx={{ minWidth: 35 }}>
                                                    <Typography variant="body2" color="text.secondary">{`${Math.round(progress)}%`}</Typography>
                                                </Box>
                                            </Box>
                                        </Grid> :
                                        <MDTypography component='span' variant='body' fontSize='0.93rem' fontWeight='regular'>
                                            <p style={{ marginBottom: "0.2rem" }}> Click<Button variant="text" onClick={onExportData} size="small" style={{ fontSize: "inherit", padding: "0.25rem", paddingBottom: "0.4rem", minWidth: "0", textTransform: "none" }}>here</Button>
                                                to Export data to a file</p>
                                        </MDTypography>
                                    }
                                </MDAlert>
                            }
                        </MDBox>
                    </Grid>
                </Grid>
                <ConfirmationDialog
                    isLoading={isConfirmationLoading}
                    showDialog={showConfirmationDialog}
                    handleAccept={onDeletePeopleRow}
                    handleCancel={handleCloseConfirmation}
                    heading={"Confirm Delete"}
                    message={"Are you sure you want to delete this person?"}
                />
                <InformationDialog
                    handleAccept={() => setShowInformationDialog(false)}
                    heading={"Unable To Delete Person"}
                    message={informationMessage}
                    messageList={scorecardNames}
                    showDialog={showInformationDialog}
                />


            </>
        );
    }
}
